import { Cmd, loop } from 'redux-loop';
import { getActiveResources, isBeforeOrEqual, getNow } from '@kathondvla/sri-client/date-utils';
import { settings } from 'Js/config/settings';
import { mapOuType } from 'ReduxLoop/constants';
import * as COMMANDS from './helpdeskCommands';
import * as ACTIONS from './helpdeskActions';

const initialState = {
  searchValue: null,
  nbOfResultsShown: 30,
  ouType: null,
  institutionNumberSearch: false,
  abolishedInstitution: null,
  schoolWithRelatedSes: null,
  searchingForInstitutionNumber: false,
};

export const helpdeskTypeConfig = {
  PRIMARY_SCHOOL: {
    label: 'basisscholen',
    domain: 'sam',
    types: ['SCHOOL'],
    mainstructures: ['111', '121', '211', '221'],
    path: null,
  }, 
  SECONDARY_SCHOOL: {
    label: 'secundaire scholen',
    domain: 'sam',
    types: ['SCHOOLENTITY'],
    mainstructures: ['311', '312', '316', '321'],
    path: 'school'
  },
  HIGHER_EDUCATION_SCHOOL: {
    label: 'hoger onderwijs',
    domain: 'sam',
    types: ['SCHOOL'],
    mainstructures: ['411', '511'],
    path: null,
  },
  CVO: { 
    label: 'CVO\'s',
    domain: 'sam',
    types: ['SCHOOL'],
    mainstructures: ['317', '417', '618'],
    path: null,
  },
  BOARDING: {
    label: 'internaten',
    domain: 'sam',
    types: ['BOARDING'],
    path: null,
  },
  GOVERNINGINSTITUTION: {
    label: 'besturen',
    domain: 'sam',
    types: ['GOVERNINGINSTITUTION'],
    path: 'bestuur',
  },
  CLB: {
    label: 'CLB\'s',
    domain: 'sam',
    types: ['CLB'],
    path: 'clb',
  },
  SCHOOLCOMMUNITY: {
    label: 'scholengemeenschappen',
    domain: 'sam',
    types: ['SCHOOLCOMMUNITY'],
    path: null,
  },
  LEERSTEUNCENTRUM: {
    label: 'leersteuncentra',
    domain: 'sam',
    types: ['ALGEMEEN_LEERSTEUNCENTRUM', 'SPECIFIEK_LEERSTEUNCENTRUM'],
    path: 'leersteuncentrum',
  },
  EXTERN: {
    label: 'organisaties',
    domain: 'kathondvla',
    types: ['EXTERN', 'BISDOM'],
    path: 'organisatie',
  },
  LERARENNETWERK: {
    label: 'lerarennetwerken',
    domain: 'kathondvlasam',
    types: ['LERARENNETWERK'],
    path: 'lerarennetwerk',
  },
  TEAMS: {
    label: 'teams',
    domain: 'kathondvla',
    types: ['VERGADERGROEP', 'LEERPLANCOMMISSIE'],
    path: 'team',
  },
  ONDERWIJSKOEPEL: {
    label: 'onderwijskoepels',
    domain: 'sam',
    types: ['ONDERWIJSKOEPEL'],
    path: 'organisatie',
  }
};

Object.keys(helpdeskTypeConfig).forEach((key) => {
  initialState[key] = null;
});

const mapOu = (ou, mailAddresses, identifiers, ouType) => {
  const hdtConfig = helpdeskTypeConfig[ouType];
  const addresses = mailAddresses
    .filter((address) => address.organisationalUnit.href === ou.$$meta.permalink)
    .map((address) => address.value);
  const identifierObj = identifiers
    .find((instNr) => instNr.organisationalUnit.href === ou.$$meta.permalink);
  const identifier = identifierObj ? identifierObj.value : null;
  return {
    key: ou.key,
    code: ou.code,
    type: mapOuType(ou.type),
    name: ou.$$displayName,
    identifier,
    addresses,
    href: hdtConfig.path
      ? `/#!/${hdtConfig.path}/${ou.key}/team`
      : `${settings.applications.mijn1}/#/team/${identifier}`,
  };
}

export const helpdeskReducer = (state = initialState, action) => {
  switch (action.type) {
    case ACTIONS.SEARCH_VALUE_CHANGED: {
      const newSearchValue = action.payload ? action.payload.trim() : action.payload;
      const newState = {
        ...state,
        searchValue: newSearchValue,
        nbOfResultsShown: initialState.nbOfResultsShown,
        abolishedInstitution: null,
        secondaryInstitution: null,
        searchingForInstitutionNumber: false,
        institutionNumberSearch: false,
      };
      if (newSearchValue && newSearchValue.match(/\d{6}/)) {
        console.log('een instellingsnummer!');
        newState.institutionNumberSearch = true;
        newState.searchingForInstitutionNumber = true;
        return loop(
          newState,
          Cmd.run(COMMANDS.getOuByInstitutionNumber, {
            args: [newSearchValue, state],
            successActionCreator: ACTIONS.ouFetchedByInstitutionNumber,
          })
        );
      }
      if (!state[state.ouType]) {
        return loop(newState, Cmd.action(ACTIONS.ouTypeSearchFilterChanged(state.ouType)));
      }
      return newState;
    }

    case ACTIONS.SHOW_MORE_RESULTS: {
      return { ...state, nbOfResultsShown: state.nbOfResultsShown + 30 };
    }

    case ACTIONS.OU_TYPE_SEARCH_FILTER_CHANGED: {
      const newOuType = action.payload;
      const newState = { ...state, ouType: newOuType };
      if (state[newOuType]) {
        return newState;
      }
      const hdtConfig = helpdeskTypeConfig[newOuType];
      if (hdtConfig.domain === 'sam') {
        return loop(
          newState,
          Cmd.run(COMMANDS.getAllSamOusOfType, {
            args: [hdtConfig.types, hdtConfig.mainstructures],
            successActionCreator: (results) => ACTIONS.ousFetchedForHelpdesk(results, newOuType),
          })
        );
      }
      // else kathondvla
      return loop(
        newState,
        Cmd.run(COMMANDS.getKathOndVlaOusOfType, {
          args: [hdtConfig.types],
          successActionCreator: (results) => ACTIONS.ousFetchedForHelpdesk(results, newOuType),
        })
      );
    }

    case ACTIONS.OUS_FETCHED_FOR_HELPDESK: {
      const { results, ouType } = action.payload;
      const newState = {
        ...state,
        [ouType]: results.ous.map((ou) => mapOu(ou, results.mailAddresses, results.identifiers, ouType)),
      };
      return newState;
    };

    case ACTIONS.OU_FETCHED_BY_INSTITUTION_NUMBER: {
      if (!action.payload) {
        return { ...state, searchingForInstitutionNumber: false };
      }
      const institutionNumber = action.payload.institutionNumber.value;
      if (institutionNumber !== state.searchValue) {
        return state;
      }
      const ou = action.payload.institutionNumber.organisationalUnit.$$expanded;
      if (isBeforeOrEqual(ou.endDate, getNow())) {
        console.warn('The user is searching for an abolished institution', ou);
        return {
          ...state,
          searchingForInstitutionNumber: false,
          abolishedInstitution: {
            key: ou.key,
            type: ou.type,
            name: ou.$$displayName,
            identifier: institutionNumber,
            endDate: ou.endDate,
            addresses: [],
          },
        };
      }
      const { mainstructures, relatedSchoolEntities, mailAddresses } = action.payload;
      const newState = { ...state, searchingForInstitutionNumber: false };
      let ouType = Object.values(helpdeskTypeConfig).find((config) =>
        config.types.includes(ou.type) && (!mainstructures || (config.mainstructures && mainstructures.some((ms) => config.mainstructures.includes(ms))))
      );
      if (mainstructures && mainstructures[0].match(/^3/)) {
        ouType = 'SECONDARY_SCHOOL';
        newState.schoolWithRelatedSes = {
          key: ou.key,
          type: 'OFFICIAL_SCHOOL',
          name: ou.$$displayName,
          identifier: institutionNumber,
          addresses: mailAddresses.map((address => address.value)),
          relatedSchoolEntities: relatedSchoolEntities,
          href: `${settings.applications.mijn1}/#/team/${action.payload.institutionNumber}`,
        };
      }
      return loop(
        newState,
        Cmd.action(ACTIONS.ouTypeSearchFilterChanged(ouType))
      );
    }

    default:
      return state;
  }
};